import { setFormError, updateFormData } from 'js/actions';
import { MonacoCodeEditorOnGetMarkersParam } from 'js/components/elements/editors/monaco/MonacoCodeEditor';
import { FORM_TYPE_CREATE_REUSABLE_TX as FormKey } from 'js/constants/formTypes';
import FunctionArgs from 'new/components/ReusableTransform/FunctionArgs';
import InputTestSample from 'new/components/ReusableTransform/InputTestSample';
import { ReusableTransformProps } from 'new/components/ReusableTransform/ReusableTransform';
import { TransformDataForm, TransformType } from 'new/components/ReusableTransform/types';
import { useGetForm } from 'new/hooks/useGetForm';
import { ReusableTransformUtil } from 'new/utils/ReusableTransform/argumentValues';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Form } from 'src/@types';
import './ReusableCore.scss';
import ReusableCoreHeader from './ReusableCoreHeader';
import useEntities from 'new/state/entities/repository/useEntities';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_CODE_CONTAINER_REQUEST } from 'js/constants/actionTypes';

const DEFAULT_NAME = 'Untitled Transform';

function ReusableCore(props: ReusableTransformProps) {
  const [sampleInputErrors, setSampleInputError] = useState<MonacoCodeEditorOnGetMarkersParam>([]);

  const dispatch = useDispatch();
  const { data: transformsMap } = useEntities('transforms');
  const loadingContainer = useIsLoading(GET_CODE_CONTAINER_REQUEST);
  const form = useGetForm<Form<TransformDataForm>>(FormKey);
  const code = form?.data?.code || '';
  const inputCode = form?.data.input;
  const language = form?.data.codeLanguage;

  const inputAttrs = form?.data.inputAttrs ?? [];
  const transformType = form?.data.transformType;
  const isRecordTx = transformType === TransformType.RECORD;

  const argumentValuesVersion = form?.data?.txArgs?.v;
  const argumentValues = form?.data?.txArgs?.items || [];
  const argumentsTestData = form?.data.txArgsTestData || [];

  const onChangeCode = (value: string) => {
    dispatch(updateFormData(FormKey, { code: value }));
  };

  useEffect(() => {
    if (props.transformBeingEditedID && !loadingContainer) {
      const transform = transformsMap[props.transformBeingEditedID];
      dispatch(
        updateFormData(FormKey, {
          name: transform?.name,
          description: transform?.description,
        }),
      );
    }
  }, [props.transformBeingEditedID, loadingContainer]);

  function updateInputSampleCode(sampleCode: string) {
    dispatch(
      updateFormData(FormKey, {
        input: sampleCode,
        selectedDataSetId: null,
        inputAttrs: ReusableTransformUtil.generatingInputAttributesFor(sampleCode),
      }),
    );
  }

  const handleRunTransform = () => {
    if (sampleInputErrors.length) {
      const [primaryError] = sampleInputErrors;
      dispatch(setFormError(FormKey, primaryError.errorMessage));
      return;
    }

    props.runTransform();
  };

  return (
    <div className="reusable-core-container">
      <ReusableCoreHeader transformID={props?.transformBeingEditedID} />

      <FunctionArgs
        code={code}
        isRecordType={isRecordTx}
        onChangeCode={onChangeCode}
        language={language}
        inputCode={inputCode}
        transformType={transformType}
        argumentValues={argumentValues}
        argumentsTestData={argumentsTestData}
        argumentValuesVersion={argumentValuesVersion}
        runTransform={handleRunTransform}
        isFetchingOutputSamples={props.isFetchingOutputSamples}
        isCapsuleCluster={props.isCapsuleCluster}
      />

      <InputTestSample
        transformId={props.transformBeingEditedID}
        inputAttrs={inputAttrs}
        inputCode={inputCode}
        isRecordType={isRecordTx}
        onChangeInputCode={updateInputSampleCode}
        argumentValues={argumentValues}
        argumentsTestData={argumentsTestData}
        onChangeMarkers={setSampleInputError}
      />
    </div>
  );
}

export default ReusableCore;
